import React from 'react';
import { Grid } from '@mui/material';
import {
  asPercentageRounded,
  numberWithCommasOrDash,
  textOrDash,
} from 'spw-shared/utils';
import { useSelector } from 'react-redux';
import useUnitConversion from 'spw-components/unit-conversion/hooks';
import { selectMeasurementSystem } from 'spw-redux/modules/user/selectors';
import {
  CardTitle,
  BreakdownRow,
  BreakdownColumn,
  Pill,
  BreakdownTotalColumn,
  BreakdownCardGrid,
  PercentValue,
} from './styles';
import { SummaryCalculator } from '../../../../models/ProgramSummaryCalculator';
import { BreakdownBottomRow } from '../components/styles';
import { MeasurementSystem } from '../../../../components/unit-conversion/types';

interface IUsfBreakdown {
  showRsf: boolean;
  testingPrefix: string;
  summaryCalculator: SummaryCalculator;
}

export const UsfBreakdown: React.FunctionComponent<IUsfBreakdown> = ({
  showRsf,
  testingPrefix,
  summaryCalculator,
}) => {
  const { getUnit, convertArea } = useUnitConversion();
  const measurementSystem = useSelector(selectMeasurementSystem);
  const {
    subtotalProgramUSF,
    circulationPercentage,
    circulationUSF,
    additionalUSFForPlanningFactor,
    totalProgramRSF,
    totalProgramUSF,
    planningFactorPercentage,
    rentableLoadFactor,
    rsfLoadFactorPercentage,
  } = summaryCalculator;

  return (
    <BreakdownCardGrid style={{ display: 'flex', flexDirection: 'column' }}>
      <CardTitle data-testid={`${testingPrefix}_title`}>
        SQUARE{' '}
        {measurementSystem === MeasurementSystem.imperial
          ? 'FOOTAGE'
          : 'METERS'}{' '}
        BREAKDOWN
      </CardTitle>
      <Grid container style={{ flexGrow: 1 }}>
        <Grid item xs={12} style={{ display: 'flex', flexDirection: 'column' }}>
          <BreakdownRow>
            <BreakdownColumn item xs={8}>
              Subtotal Program {getUnit('area')}
            </BreakdownColumn>
            <BreakdownColumn
              item
              xs={4}
              className="right"
              data-testid={`${testingPrefix}_subTotalProgramUsf`}
            >
              {numberWithCommasOrDash(convertArea(subtotalProgramUSF))}
            </BreakdownColumn>
          </BreakdownRow>

          <BreakdownRow>
            <BreakdownColumn item xs={8}>
              <Pill color="transparent">
                Circulation @
                <PercentValue
                  data-testid={`${testingPrefix}_circulationPercent`}
                >
                  {circulationPercentage
                    ? asPercentageRounded(circulationPercentage / 100)
                    : textOrDash(circulationPercentage)}
                </PercentValue>
              </Pill>
            </BreakdownColumn>
            <BreakdownColumn
              item
              xs={4}
              className="right"
              data-testid={`${testingPrefix}_circulation`}
            >
              {numberWithCommasOrDash(convertArea(circulationUSF))}
            </BreakdownColumn>
          </BreakdownRow>

          <BreakdownRow className="last">
            <BreakdownColumn item xs={8}>
              <Pill color="transparent">
                Planning Factor @
                <PercentValue
                  data-testid={`${testingPrefix}_planningFactorPercent`}
                >
                  {planningFactorPercentage
                    ? asPercentageRounded(planningFactorPercentage / 100)
                    : textOrDash(planningFactorPercentage)}
                </PercentValue>
              </Pill>
            </BreakdownColumn>
            <BreakdownColumn
              item
              xs={4}
              className="right"
              data-testid={`${testingPrefix}_planningFactor`}
            >
              {numberWithCommasOrDash(
                convertArea(additionalUSFForPlanningFactor)
              )}
            </BreakdownColumn>
          </BreakdownRow>

          <Grid container>
            <BreakdownTotalColumn item xs={8}>
              Total Program {getUnit('area')}
            </BreakdownTotalColumn>
            <BreakdownTotalColumn
              item
              xs={4}
              className="right"
              data-testid={`${testingPrefix}_totalProgramUSF`}
            >
              {numberWithCommasOrDash(convertArea(totalProgramUSF))}
            </BreakdownTotalColumn>
          </Grid>

          {showRsf && (
            <div style={{ marginTop: 'auto' }}>
              <BreakdownRow className="bottom-and-separated">
                <BreakdownColumn item xs={8}>
                  <Pill color="transparent">
                    Rentable Load Factor @
                    <PercentValue>
                      {asPercentageRounded(rsfLoadFactorPercentage / 100, true)}
                    </PercentValue>
                  </Pill>
                </BreakdownColumn>
                <BreakdownColumn item xs={4} className="right">
                  {numberWithCommasOrDash(rentableLoadFactor)}
                </BreakdownColumn>
              </BreakdownRow>
              <BreakdownBottomRow>
                <BreakdownColumn item xs={8}>
                  <Pill color="transparent">Total Program RSF</Pill>
                </BreakdownColumn>
                <BreakdownColumn item xs={4} className="right">
                  {numberWithCommasOrDash(
                    rentableLoadFactor ? convertArea(totalProgramRSF) : 0
                  )}
                </BreakdownColumn>
              </BreakdownBottomRow>
            </div>
          )}
        </Grid>
      </Grid>
    </BreakdownCardGrid>
  );
};
